import { useMemo, useRef, useState } from "react";
import { Camera, Trash2, Truck } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { DateInput } from "@/components/ui/DateInput";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import { currentLocalDateInput } from "@/utils/dateInput";
import { formatNumber } from "@/utils/format";
import { upperLocacaoText } from "./upperLocacaoText";
import {
  CARRADA_MATERIAIS,
  carradaFotoUrl,
  deleteCarradaFoto,
  uploadCarradaFoto,
  type CaminhaoCarradasDto,
  type CarradaDto,
  type CarradaFotoDto,
  type CarradaFormValues,
  type ContratoCarradasDto,
} from "@/services/carradas";
import { extractApiError, getValorCarradaVigente } from "@/services/locacoes";

/** Aceita image/* e arquivos sem MIME (comum no Windows) com extensão de imagem. */
function isImageFile(file: File): boolean {
  const mt = (file.type || "").toLowerCase();
  if (mt.startsWith("image/")) return true;
  if (mt && mt !== "application/octet-stream") return false;
  return /\.(jpe?g|png|webp|gif|heic|heif|bmp)$/i.test(file.name || "");
}

function parseQuantidadeInteira(raw: unknown): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return null;
  const q = Math.round(n);
  if (Math.abs(n - q) > 1e-9) return null;
  return q;
}

function emptyForm(): CarradaFormValues {
  return {
    contratoId: "",
    caminhaoId: "",
    data: currentLocalDateInput(),
    quantidade: 1,
    material: "",
    motorista: "",
    observacao: "",
  };
}

function fromDto(c: CarradaDto): CarradaFormValues {
  return {
    contratoId: c.contratoId,
    caminhaoId: c.caminhaoId,
    data: c.data,
    quantidade: c.quantidade,
    material: c.material || "",
    motorista: upperLocacaoText(c.motorista),
    observacao: upperLocacaoText(c.observacao),
  };
}

interface Props {
  open: boolean;
  editing: CarradaDto | null;
  contratos: ContratoCarradasDto[];
  caminhoes: CaminhaoCarradasDto[];
  busy?: boolean;
  onClose: () => void;
  onSave: (values: CarradaFormValues, editingId: string | null) => Promise<CarradaDto | void>;
  onDelete?: () => void;
}

export function ModalCarrada({
  open,
  editing,
  contratos,
  caminhoes,
  busy,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const [values, setValues] = useState<CarradaFormValues>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [fotos, setFotos] = useState<CarradaFotoDto[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<Array<{ file: File; preview: string }>>([]);
  const [fotoBusy, setFotoBusy] = useState(false);
  /** Id local após criar na mesma sessão do modal (antes do parent atualizar `editing`). */
  const [createdId, setCreatedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const recordId = editing?.id ?? createdId;
  const totalPhotos = fotos.length + pendingPhotos.length;

  useModalFormSession(open, editing?.id ?? "new", () => {
    setValues(editing ? fromDto(editing) : emptyForm());
    setFotos(editing?.fotos ?? []);
    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setCreatedId(null);
    setError(null);
    setFotoBusy(false);
  });

  const contratoOptions = useMemo(() => {
    const opts = contratos.map((c) => ({
      value: c.id,
      label: `${c.codigoContrato || c.id} · ${c.clienteSiengeNome}`,
    }));
    if (
      editing?.contratoId &&
      !opts.some((o) => o.value === editing.contratoId)
    ) {
      opts.unshift({
        value: editing.contratoId,
        label: `${editing.contratoId} (contrato do lançamento)`,
      });
    }
    return opts;
  }, [contratos, editing]);

  const contratoSelecionado = useMemo(
    () => contratos.find((c) => c.id === values.contratoId) ?? null,
    [contratos, values.contratoId],
  );

  const caminhoesDoContrato = useMemo(() => {
    if (!contratoSelecionado) return [];
    const ids = new Set(contratoSelecionado.maquinaIds);
    return caminhoes.filter((c) => ids.has(c.id));
  }, [caminhoes, contratoSelecionado]);

  const valorUnitarioVigente = useMemo(() => {
    if (!contratoSelecionado) return null;
    return getValorCarradaVigente(contratoSelecionado.valorCarradaVigencias);
  }, [contratoSelecionado]);

  const handleContratoChange = (contratoId: string) => {
    setValues((v) => {
      const contrato = contratos.find((c) => c.id === contratoId);
      const ids = new Set(contrato?.maquinaIds ?? []);
      const caminhaoId = ids.has(v.caminhaoId) ? v.caminhaoId : "";
      return {
        ...v,
        contratoId,
        caminhaoId,
      };
    });
  };

  const handlePickPhotos = (fileList: FileList | File[]) => {
    const selected = Array.from(fileList).filter(isImageFile);
    if (selected.length === 0) {
      setError("Selecione ao menos uma imagem válida (JPEG, PNG, WebP ou GIF).");
      return;
    }
    const pending = selected.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPendingPhotos((prev) => [...prev, ...pending]);
    setError(null);
  };

  const handleRemovePendingPhoto = (index: number) => {
    setPendingPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const uploadPendingPhotos = async (carradaId: string): Promise<CarradaFotoDto[]> => {
    if (pendingPhotos.length === 0) return [];
    const uploaded: CarradaFotoDto[] = [];
    for (const p of pendingPhotos) {
      const foto = await uploadCarradaFoto(carradaId, p.file);
      uploaded.push(foto);
    }
    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setFotos((prev) => [...prev, ...uploaded]);
    return uploaded;
  };

  const handleDeleteFoto = async (fotoId: string) => {
    if (!recordId) return;
    setFotoBusy(true);
    setError(null);
    try {
      await deleteCarradaFoto(recordId, fotoId);
      setFotos((prev) => prev.filter((f) => f.id !== fotoId));
    } catch (e) {
      setError(extractApiError(e, "Erro ao excluir foto."));
    } finally {
      setFotoBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!values.contratoId) {
      setError("Selecione o contrato.");
      return;
    }
    if (!values.caminhaoId) {
      setError("Selecione o caminhão.");
      return;
    }
    if (!values.data) {
      setError("Informe a data.");
      return;
    }
    const qtdInt = parseQuantidadeInteira(values.quantidade);
    if (qtdInt == null) {
      setError("Quantidade deve ser um número inteiro maior que zero.");
      return;
    }
    if (!values.material.trim()) {
      setError("Selecione o material carregado.");
      return;
    }
    if (valorUnitarioVigente == null) {
      setError("Não há valor unitário vigente para esta data no contrato.");
      return;
    }
    if (totalPhotos === 0) {
      setError("Anexe ao menos uma foto da caçamba.");
      return;
    }
    setError(null);
    const payload: CarradaFormValues = {
      ...values,
      quantidade: qtdInt,
    };

    try {
      const existingId = editing?.id ?? createdId;

      if (existingId) {
        // Edição: envia as fotos pendentes antes de salvar o lançamento.
        if (pendingPhotos.length > 0) {
          setFotoBusy(true);
          try {
            await uploadPendingPhotos(existingId);
          } finally {
            setFotoBusy(false);
          }
        }
        await onSave(payload, existingId);
      } else {
        const saved = await onSave(payload, null);
        const savedId = saved?.id;
        if (!savedId) {
          setError("Falha ao criar o lançamento.");
          return;
        }
        setCreatedId(savedId);
        if (pendingPhotos.length > 0) {
          setFotoBusy(true);
          try {
            await uploadPendingPhotos(savedId);
          } finally {
            setFotoBusy(false);
          }
        }
      }
      onClose();
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar."));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-brand-600" />
          <span>{recordId ? "Editar carrada" : "Nova carrada"}</span>
        </div>
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {editing && onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                disabled={busy || fotoBusy}
                className="text-red-600 hover:text-red-700"
              >
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy || fotoBusy}>
              Cancelar
            </Button>
            <Button onClick={() => void handleSubmit()} loading={busy || fotoBusy} disabled={fotoBusy}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Select
          label="Contrato *"
          options={contratoOptions}
          value={values.contratoId}
          onChange={(e) => handleContratoChange(e.target.value)}
          placeholder={
            contratoOptions.length === 0
              ? "Nenhum contrato por carradas ativo"
              : "Selecione o contrato"
          }
        />
        <Select
          label="Caminhão *"
          options={
            caminhoesDoContrato.length > 0
              ? caminhoesDoContrato.map((c) => ({
                  value: c.id,
                  label: `${c.nome}${c.placa ? ` · ${c.placa}` : ""}`,
                }))
              : caminhoes
                  .filter((c) => !values.contratoId || c.id === values.caminhaoId)
                  .map((c) => ({
                    value: c.id,
                    label: `${c.nome}${c.placa ? ` · ${c.placa}` : ""}`,
                  }))
          }
          value={values.caminhaoId}
          onChange={(e) => setValues((v) => ({ ...v, caminhaoId: e.target.value }))}
          placeholder={
            !values.contratoId
              ? "Selecione o contrato primeiro"
              : caminhoesDoContrato.length === 0
                ? "Contrato sem caminhões vinculados"
                : "Selecione o caminhão"
          }
        />
        <DateInput
          label="Data *"
          value={values.data}
          onChange={(iso) => setValues((v) => ({ ...v, data: iso }))}
        />
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
          <div className="text-sm text-slate-700">
            Valor unitário vigente no cadastro:{" "}
            <span className="font-semibold">
              {valorUnitarioVigente != null ? `R$ ${formatNumber(valorUnitarioVigente, 2)}` : "—"}
            </span>
          </div>
          <p className="text-xs text-slate-500">
            {editing ? (
              <>
                Valor do lançamento:{" "}
                <span className="font-medium">R$ {formatNumber(editing.valorUnitario, 2)}</span>.
              </>
            ) : (
              <>O lançamento usa o valor vigente no momento do salvamento.</>
            )}{" "}
            Reajustes são feitos no detalhe do contrato (Registrar reajuste) e passam a valer para carradas
            seguintes.
          </p>
        </div>
        <Input
          label="Quantidade (carradas) *"
          type="number"
          min={1}
          step={1}
          value={String(values.quantidade)}
          onChange={(e) => setValues((v) => ({ ...v, quantidade: Number(e.target.value) || 0 }))}
        />
        <Select
          label="Material *"
          options={(() => {
            const opts: Array<{ value: string; label: string }> = CARRADA_MATERIAIS.map((m) => ({
              value: m,
              label: m,
            }));
            if (values.material && !opts.some((o) => o.value === values.material)) {
              opts.unshift({ value: values.material, label: values.material });
            }
            return opts;
          })()}
          value={values.material}
          onChange={(e) => setValues((v) => ({ ...v, material: e.target.value }))}
          placeholder="Material carregado"
        />
        <Input
          label="Operador"
          uppercase
          value={values.motorista}
          onChange={(e) => setValues((v) => ({ ...v, motorista: e.target.value }))}
          hint="Operador/motorista deste lançamento (não altera o cadastro da máquina)."
        />
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observação</label>
          <textarea
            value={values.observacao}
            onChange={(e) => setValues((v) => ({ ...v, observacao: upperLocacaoText(e.target.value) }))}
            rows={2}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Foto da caçamba *
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leftIcon={<Camera className="h-3.5 w-3.5" />}
              disabled={fotoBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              Anexar fotos
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                // Copiar antes de limpar: FileList é vivo e zera com value="".
                const files = Array.from(e.target.files ?? []);
                e.target.value = "";
                if (files.length) handlePickPhotos(files);
              }}
            />
          </div>
          <p className="text-xs text-slate-500">
            Obrigatório — anexe ao menos uma foto da caçamba. A quantidade de fotos é livre e
            independente da quantidade de carradas. Pode selecionar várias de uma vez.
            {totalPhotos > 0 ? ` Anexadas: ${totalPhotos}.` : ""}
          </p>
          <div className="flex flex-wrap gap-2">
            {fotos.map((f) => (
              <div key={f.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-slate-200">
                <img
                  src={recordId ? carradaFotoUrl(recordId, f.id) : ""}
                  alt={f.originalName}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  className="absolute right-0.5 top-0.5 rounded bg-white/90 p-0.5 text-red-600"
                  onClick={() => void handleDeleteFoto(f.id)}
                  disabled={fotoBusy || !recordId}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
            {pendingPhotos.map((p, index) => (
              <div key={`p-${index}`} className="relative h-20 w-20 overflow-hidden rounded-lg border border-slate-200">
                <img src={p.preview} alt="Prévia" className="h-full w-full object-cover" />
                <button
                  type="button"
                  className="absolute right-0.5 top-0.5 rounded bg-white/90 p-0.5 text-red-600"
                  onClick={() => handleRemovePendingPhoto(index)}
                  disabled={fotoBusy}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
